import type { DocumentDto } from "../../../services/documentService";
import { DOKUMENT_KATEGORIEN } from "../../../data/dokumentKategorien";
import { KATEGORIE_MAIL_ARCHIV } from "../../vorgang/detail/terminNotizen";
import { KATEGORIE_NOTIZBILD } from "../../notizbloecke/bildDarstellung";

/**
 * Die rechnende Hälfte der Dokument-Auswahl: Gruppierung, Format-Prüfung,
 * Größenangabe. Bewusst ohne React — das sind die Stellen, an denen sich ein
 * Fehler still einschleicht (ein Ordnerpfad, der anders zerfällt als gedacht;
 * ein `accept`, das zu viel ausgraut), und die prüft man am billigsten ohne
 * gerendertes Bauteil.
 */

/**
 * Nicht anbieten: die dauerhaft abgelegte `.eml` einer archivierten Mail
 * (an ihr hängt der eigene Zugriffsschutz) und die Bilder aus Notizblöcken
 * (die gehören in ihre Notiz, nicht in eine Anhangsliste). Wer es anders
 * braucht, übergibt eine eigene Liste.
 */
export const STANDARD_AUSGESCHLOSSENE_KATEGORIEN: readonly string[] = [
    KATEGORIE_MAIL_ARCHIV,
    KATEGORIE_NOTIZBILD,
];

/**
 * Das Kategorie-Dropdown über der Liste. Es FILTERT nur — anders als
 * `DOKUMENT_KATEGORIEN`, das sagt, was man vergeben darf. Deshalb stehen hier
 * zusätzlich die maschinell gesetzten Werte: sie machen den Großteil des
 * Bestands aus (PROD 15.08.2026: 110.999 „Alte Kundenakte", 17.374 „Schaden"),
 * und ohne sie wäre das Dropdown an einer Altakte wertlos.
 */
export const KATEGORIE_FILTER: readonly { value: string; text: string }[] = [
    { value: "", text: "Alle Kategorien" },
    ...DOKUMENT_KATEGORIEN.map((k) => ({ value: k.value, text: k.value })),
    { value: "Alte Kundenakte", text: "Alte Kundenakte" },
    { value: "Schaden", text: "Schaden" },
    { value: "E-Mail-Anhang", text: "E-Mail-Anhang" },
];

/** Dateigröße in der Schreibweise der Anhangsliste (1,4 MB). */
export const formatiereGroesse = (bytes: number | null | undefined): string => {
    if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    const einheiten = ["KB", "MB", "GB"];
    let wert = bytes / 1024;
    let i = 0;
    while (wert >= 1024 && i < einheiten.length - 1) {
        wert /= 1024;
        i++;
    }
    return `${wert.toLocaleString("de-DE", { maximumFractionDigits: wert < 10 ? 1 : 0 })} ${einheiten[i]}`;
};

/**
 * Passt das Dokument zu einem `accept`-Ausdruck (`"application/pdf,image/*"`,
 * `".pdf,.jpg"`)? Ohne `accept` passt alles.
 *
 * Geprüft wird gegen den `mimeType` UND die Endung des Dateinamens: der
 * Bestand trägt für einen Teil der Dokumente gar keinen `mimeType` (Import
 * der Alten Kundenakte), und für die wäre eine reine MIME-Prüfung ein
 * pauschales „passt nicht".
 */
export const passtZuAccept = (
    doc: { mimeType?: string | null; fileName?: string | null },
    accept?: string,
): boolean => {
    const muster = (accept ?? "")
        .split(",")
        .map((m) => m.trim().toLowerCase())
        .filter(Boolean);
    if (muster.length === 0) return true;

    const mime = (doc.mimeType ?? "").trim().toLowerCase();
    const name = (doc.fileName ?? "").toLowerCase();
    const punkt = name.lastIndexOf(".");
    const endung = punkt > 0 ? name.slice(punkt) : "";

    return muster.some((m) => {
        if (m === "*/*") return true;
        if (m.startsWith(".")) return endung === m;
        if (m.endsWith("/*")) return mime.startsWith(m.slice(0, -1));
        return mime === m;
    });
};

/** Eine Gruppe in der Liste: Kopfzeile plus die Dokumente darunter. */
export type DokumentGruppe = {
    /** Stabil über Neuladen hinweg — React-`key` und Aufklapp-Zustand hängen daran. */
    key: string;
    titel: string;
    /** Zweite Zeile der Kopfzeile, z. B. der Vorgangstitel. */
    unterzeile?: string;
    dokumente: DocumentDto[];
};

/**
 * Kundendokumente: nach dem Ordner der Alten Kundenakte gruppiert.
 *
 * Der Ordner steckt im `fileName` (`Vertraege/2019/police.pdf`) — es gibt
 * keine Ordner-Tabelle, der Explorer der Kundenakte baut seinen Baum genauso.
 * Dateien ohne "/" liegen an der Wurzel und kommen als erste Gruppe ohne
 * Kopfzeile. Die Reihenfolge der Dokumente bleibt, wie der Server sie liefert
 * (`uploadedAt` absteigend).
 */
export const gruppiereNachOrdner = (dokumente: DocumentDto[]): DokumentGruppe[] => {
    const gruppen = new Map<string, DokumentGruppe>();
    for (const doc of dokumente) {
        const name = doc.fileName ?? "";
        const strich = name.lastIndexOf("/");
        const ordner = strich > 0 ? name.slice(0, strich) : "";
        const vorhanden = gruppen.get(ordner);
        if (vorhanden) vorhanden.dokumente.push(doc);
        else gruppen.set(ordner, { key: `ordner:${ordner}`, titel: ordner, dokumente: [doc] });
    }
    // Die Wurzel zuerst, danach die Ordner alphabetisch — so steht das zuletzt
    // Hochgeladene oben und der Altbestand darunter.
    return [...gruppen.values()].sort((a, b) => {
        if (a.titel === b.titel) return 0;
        if (!a.titel) return -1;
        if (!b.titel) return 1;
        return a.titel.localeCompare(b.titel, "de");
    });
};

/**
 * Vorgangsdokumente: nach dem Vorgang gruppiert, der neueste zuerst.
 *
 * „Neueste zuerst" heißt hier: in der Reihenfolge, in der die Vorgänge in der
 * Liste zum ersten Mal auftauchen. Der Server sortiert nach `uploadedAt`
 * absteigend, also steht der Vorgang mit dem jüngsten Dokument oben — ohne
 * dass die Liste ein Vorgangsdatum bräuchte, das sie gar nicht mitliefert.
 */
export const gruppiereNachVorgang = (dokumente: DocumentDto[]): DokumentGruppe[] => {
    const gruppen = new Map<string, DokumentGruppe>();
    for (const doc of dokumente) {
        const vorgang = doc.vorgang ?? null;
        const key = vorgang ? `vorgang:${vorgang.id}` : "vorgang:ohne";
        const vorhanden = gruppen.get(key);
        if (vorhanden) {
            vorhanden.dokumente.push(doc);
            continue;
        }
        gruppen.set(key, {
            key,
            titel: vorgang ? (vorgang.vorgangNbr ?? `Vorgang ${vorgang.id}`) : "Ohne Vorgang",
            unterzeile: vorgang?.titel ?? undefined,
            dokumente: [doc],
        });
    }
    return [...gruppen.values()];
};

/**
 * T-225: Reiter „Aus Vorgängen" mit bekanntem Vorgang — dessen Dokumente
 * stehen als erste Gruppe „Dieser Vorgang" oben, darunter die übrigen
 * Vorgänge des Kunden OHNE die schon gezeigten (sonst stünde dieselbe Datei
 * zweimal da). Leere Gruppen fallen weg.
 */
export const mitDiesemVorgang = (
    dieser: DocumentDto[],
    uebrige: DokumentGruppe[],
    unterzeile?: string,
): DokumentGruppe[] => {
    const schon = new Set(dieser.map((d) => d.id));
    const rest = uebrige
        .map((g) => ({ ...g, dokumente: g.dokumente.filter((d) => !schon.has(d.id)) }))
        .filter((g) => g.dokumente.length > 0);
    if (dieser.length === 0) return rest;
    return [{ key: "vorgang:dieser", titel: "Dieser Vorgang", unterzeile, dokumente: dieser }, ...rest];
};

/**
 * Die Spalte „Typ": ein kurzes Wort, wie es der Explorer zeigt („PDF",
 * „Word"), nicht der MIME-Typ. Maßgeblich ist die Endung; der `mimeType`
 * springt nur ein, wo der Name keine hat.
 */
export const dateiTyp = (fileName?: string | null, mimeType?: string | null): string => {
    const name = (fileName ?? "").toLowerCase();
    const punkt = name.lastIndexOf(".");
    const endung = punkt > 0 ? name.slice(punkt + 1) : "";
    const NACH_ENDUNG: Record<string, string> = {
        pdf: "PDF",
        doc: "Word",
        docx: "Word",
        xls: "Excel",
        xlsx: "Excel",
        csv: "CSV",
        ppt: "PowerPoint",
        pptx: "PowerPoint",
        eml: "E-Mail",
        msg: "E-Mail",
        txt: "Text",
        zip: "ZIP",
        jpg: "JPG",
        jpeg: "JPG",
        png: "PNG",
        gif: "GIF",
        heic: "HEIC",
        tif: "TIFF",
        tiff: "TIFF",
    };
    if (NACH_ENDUNG[endung]) return NACH_ENDUNG[endung];
    const mime = (mimeType ?? "").toLowerCase();
    if (mime === "application/pdf") return "PDF";
    if (mime.startsWith("image/")) return "Bild";
    if (endung) return endung.toUpperCase();
    return "Datei";
};

/**
 * „Geändert" wie im Explorer: heute und gestern mit Uhrzeit, alles davor als
 * Datum. `jetzt` ist nur für Tests einstellbar.
 */
export const formatiereAenderung = (
    wann: string | number | Date | null | undefined,
    jetzt: Date = new Date(),
): string => {
    if (wann == null || wann === "") return "";
    const d = new Date(wann);
    if (Number.isNaN(d.getTime())) return "";
    const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const abstand = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
    const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    if (abstand === 0) return `Heute, ${uhr}`;
    if (abstand === 1) return `Gestern, ${uhr}`;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * Die Auswahl nach einem Klick — dieselben Regeln wie im Explorer:
 * Klick markiert nur diese Zeile, Strg+Klick schaltet sie dazu oder weg,
 * Umschalt+Klick markiert den Bereich vom letzten Klick bis hier.
 *
 * Anders als im Explorer lebt die Auswahl über die Reiter hinweg (ein
 * Kundendokument und eine Datei vom Rechner in einem Rutsch). „Nur diese
 * Zeile" heißt deshalb: nur in der GERADE SICHTBAREN Liste — was in einem
 * anderen Reiter markiert ist, bleibt. `einzeln` kennt nur eine Wahl über
 * alle Reiter.
 */
export const naechsteAuswahl = (
    vorher: ReadonlySet<string>,
    key: string,
    sichtbar: readonly string[],
    anker: string | null,
    art: { strg?: boolean; umschalt?: boolean; einzeln?: boolean },
): Set<string> => {
    if (art.einzeln) return new Set([key]);
    if (art.strg && !art.umschalt) {
        const naechste = new Set(vorher);
        if (naechste.has(key)) naechste.delete(key);
        else naechste.add(key);
        return naechste;
    }
    // Strg+Umschalt ergänzt den Bereich, statt die Liste neu zu markieren.
    const naechste = art.strg
        ? new Set(vorher)
        : new Set([...vorher].filter((k) => !sichtbar.includes(k)));
    const von = anker != null ? sichtbar.indexOf(anker) : -1;
    const bis = sichtbar.indexOf(key);
    if (art.umschalt && von >= 0 && bis >= 0) {
        const [a, b] = von < bis ? [von, bis] : [bis, von];
        sichtbar.slice(a, b + 1).forEach((k) => naechste.add(k));
        return naechste;
    }
    naechste.add(key);
    return naechste;
};
